import { getResultType } from '@web-platform/shared-banana-utils-frontend/src/helpers/isBananaCombinedResults';
import { LOGGING } from '@web-platform/shared-universal-constants/src/logging';
import { MAX_RADAR_MUTATION_REQUESTS } from '@web-platform/shared-universal-constants/src/network';
import {
  RADAR_RAIL_STATUS_SESSION_ID,
  ResultStatus,
} from '@web-platform/shared-universal-constants/src/radar';
import { FEATURE_DECISION_KEYS } from '@web-platform/shared-universal-constants-config';
import { makeGetRadarMutationVariables } from '@web-platform/shared-universal-redux/src/selectors/mutationVariables';
import * as fromMutationSelectors from '@web-platform/shared-universal-redux/src/selectors/mutations';
import {
  getOrigin,
  getDestination,
  getOutboundDate,
} from '@web-platform/shared-universal-redux/src/selectors/search';
import { getPageGroup, getSession } from '@web-platform/shared-universal-redux/src/state';
import { RADAR_CREATE_SESSIONS } from '@web-platform/shared-universal-types/src/network/requestTypes';
import { getFeatureDecision } from '@web-platform/shared-universal-utils-frontend/src/config';
import {
  logError,
  logWarn,
} from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';
import { isResponseContainFlightsBody } from '@web-platform/shared-universal-utils-frontend/src/helpers/radar/flightsResponse';
import stringify from 'fast-json-stable-stringify';
import get from 'lodash/get';
import { redirect } from 'saddlebag-browser';

import { isSameOriginAndDestinationSearch } from '../../helpers/isSameOriginAndDestinationSearch';

import { isRailResponseNonRetryError } from './radarRailResponse';

import type { MutationParameters, ResultType } from './types';
import type { RadarResponsePayload } from '@web-platform/shared-universal-types/src/network/endpoints/radar/radarResponsePayloadTypes';
import type { RadarCreateSessionsQueryType } from '@web-platform/shared-universal-types/src/network/requestTypes';
import type { Action } from '@web-platform/shared-universal-types/src/redux/banana/actions/actions-banana-types';
import type { State } from '@web-platform/shared-universal-types/src/redux/banana/state/state-banana-types';
import type { RadarMutationItem } from '@web-platform/shared-universal-types/src/redux/state/network/mutations';
import type { Maybe } from '@web-platform/shared-universal-types-utils/src/utils';
import type { RadarResponseSuccess } from '@web-platform/shared-universal-utils-frontend/src/helpers/radar/flightsResponse';
import type { Store } from 'redux';

type VerticalType = 'flight' | 'rail';
type LogPropertiesType = {
  latestMutation: RadarMutationItem | undefined;
  requestCount: number;
  resultType: ResultType;
  vertical: VerticalType;
  verticalSessionId: Maybe<string>;
  sessionId: Maybe<string>;
};

const getRequestCount = fromMutationSelectors.makeGetCount();
const getMutationVariables = makeGetRadarMutationVariables();
const getLatestMutation = fromMutationSelectors.makeGetLatest<typeof RADAR_CREATE_SESSIONS>();

export const getMutationParameters = (
  state: State,
): MutationParameters<typeof RADAR_CREATE_SESSIONS> => {
  const type = RADAR_CREATE_SESSIONS;
  const variables = getMutationVariables(state);
  const origin = getOrigin(state);
  const destination = getDestination(state);
  const outboundDate = getOutboundDate(state);

  const resultType = getResultType(destination, origin, outboundDate);

  const requestCount = getRequestCount(state, {
    type,
    variables,
  });

  const latestMutation = getLatestMutation(state, {
    type,
    variables,
  });

  const { flightsSessionId, sessionId } = getSession(state, type);
  const isSameOriginAndDestination = isSameOriginAndDestinationSearch(state);
  return {
    requestCount,
    latestMutation,
    flightsSessionId,
    sessionId,
    isSameOriginAndDestination,
    resultType,
  };
};
const COMPONENT_NAME = 'webapp-actUponRadarFatalError';

const getRadarFlightsStatusMessage = (latestMutation?: RadarMutationItem) =>
  (latestMutation?.response &&
    !latestMutation.response.error &&
    get(latestMutation, 'response.body.itineraries.context.message')) ||
  null;

const getResponseMessage = (
  vertical: VerticalType,
  latestMutation: RadarMutationItem | undefined,
) => {
  const responsePaths: { [key in VerticalType]?: string } = {
    flight: 'response.body.itineraries.context',
    rail: 'response.body.rail.context',
  };
  const responsePath = responsePaths[vertical];

  const responseMessage = responsePath
    ? get(latestMutation, responsePath) || get(latestMutation, 'response.body')
    : null;

  return responseMessage;
};

const getVerticalSpecificLogProperties = (
  latestMutation: RadarMutationItem | undefined,
  vertical: VerticalType,
  verticalSessionId: Maybe<string>,
) => {
  const responseMessage = getResponseMessage(vertical, latestMutation);

  const verticalSpecificProperties = {
    [`errorSessionId_${vertical}`]: verticalSessionId || `No ${vertical} SessionId`,
    [`responseMessage_${vertical}`]:
      (responseMessage && stringify(responseMessage)) || `No ${vertical} message response`,
  };

  return verticalSpecificProperties;
};

const logProperties = ({
  latestMutation,
  requestCount,
  resultType,
  sessionId,
  vertical,
  verticalSessionId,
}: LogPropertiesType) => {
  const { response, type } = latestMutation ?? {};

  const commonProperties = {
    component: `radar-${vertical}`,
    resultType,
    errorStatus: response?.status?.toString() ?? '',
    errorSessionId: sessionId || 'No sessionId',
    errorType: type,
    headers: response?.headers ? stringify(response.headers) : 'No latestMutation.response.headers',
    requestCount: requestCount.toString(),
  };
  const verticalProperties = getVerticalSpecificLogProperties(
    latestMutation,
    vertical,
    verticalSessionId,
  );

  return {
    ...commonProperties,
    ...verticalProperties,
  };
};

const hasMutationErroredWithInvalidPayload = (latestMutation?: RadarMutationItem) => {
  if (!latestMutation || !latestMutation.response) {
    return false;
  }

  return latestMutation.response.error && latestMutation.response.status === 422;
};

const hasMutationErroredWithBadFlightsStatus = (latestMutation?: RadarMutationItem) => {
  if (!latestMutation || !latestMutation.response) {
    return false;
  }

  if (latestMutation.response.error) {
    return (
      latestMutation.response.status === null ||
      (latestMutation.response.status >= 400 &&
        latestMutation.response.status !== 403 &&
        latestMutation.response.status !== 422)
    );
  }

  // Radar is used for Combined Results focussed searches, which always contain flights, but Radar is extended to support Combined Explore unfocussed searches, which do not. We infer that it is Combined Results by the presence of the flights key. This file is currently only set up to handle Combined Results, so we exit out of its checks if it is not Combined Results. The JIRA to look at this in more detail is WOODPECKER-1065
  if (!isResponseContainFlightsBody(latestMutation.response as RadarResponseSuccess)) {
    return false;
  }

  const status = get(latestMutation, 'response.body.itineraries.context')?.status;
  return status === ResultStatus.FAILURE;
};

const hasMutationErroredWithBadRailStatus = (latestMutation?: RadarMutationItem) => {
  if (!latestMutation || !latestMutation.response) {
    return false;
  }

  return isRailResponseNonRetryError(latestMutation.response as RadarResponsePayload);
};

export const actUponRadarFatalError = (
  getMutationParams: () => MutationParameters<RadarCreateSessionsQueryType>,
  store: Store<State, Action>,
) => {
  const enableRail = getFeatureDecision(FEATURE_DECISION_KEYS.ENABLE_RAIL);

  try {
    const {
      flightsSessionId,
      isSameOriginAndDestination,
      latestMutation,
      requestCount,
      resultType,
      sessionId,
    } = getMutationParams();

    const { response } = latestMutation ?? {};
    const railSessionId = get(response, RADAR_RAIL_STATUS_SESSION_ID);

    if (
      enableRail &&
      hasMutationErroredWithBadRailStatus(latestMutation) &&
      !isSameOriginAndDestination
    ) {
      logWarn({
        ...logProperties({
          latestMutation,
          requestCount,
          resultType,
          vertical: 'rail',
          verticalSessionId: railSessionId,
          sessionId,
        }),
        message: 'Radar rail mutation network error occurred',
      });
    }

    if (
      hasMutationErroredWithBadFlightsStatus(latestMutation) &&
      (getRadarFlightsStatusMessage(latestMutation) === 'Same search origin and destination' ||
        isSameOriginAndDestination)
    ) {
      return;
    }

    if (hasMutationErroredWithInvalidPayload(latestMutation)) {
      logError({
        ...logProperties({
          latestMutation,
          requestCount,
          resultType,
          vertical: 'flight',
          verticalSessionId: flightsSessionId,
          sessionId,
        }),
        message: 'Redirecting to Houston after Radar mutation error occurred due to invalid data',
        error: new Error(`${LOGGING.HOUSTON_REDIRECT}: 422 Unprocessable Content response`),
      });
      const prevPageGroup = getPageGroup(store.getState());

      redirect(
        `/500.ashx?source=${LOGGING.PAGE_NAME}&component=${COMPONENT_NAME}&prevPageGroup=${prevPageGroup}`,
      );
    }

    if (
      requestCount >= MAX_RADAR_MUTATION_REQUESTS &&
      hasMutationErroredWithBadFlightsStatus(latestMutation)
    ) {
      logError({
        ...logProperties({
          latestMutation,
          requestCount,
          resultType,
          vertical: 'flight',
          verticalSessionId: flightsSessionId,
          sessionId,
        }),
        message: 'Redirecting to Houston after max failed Radar mutation attempts',
        error: new Error(`${LOGGING.HOUSTON_REDIRECT}: Create Session retry count exceeded`),
      });
      const prevPageGroup = getPageGroup(store.getState());

      redirect(
        `/500.ashx?source=${LOGGING.PAGE_NAME}&component=${COMPONENT_NAME}&prevPageGroup=${prevPageGroup}`,
      );
    } else if (hasMutationErroredWithBadFlightsStatus(latestMutation)) {
      logWarn({
        ...logProperties({
          latestMutation,
          requestCount,
          resultType,
          vertical: 'flight',
          verticalSessionId: flightsSessionId,
          sessionId,
        }),
        message: 'Radar flights mutation network error occurred',
      });
    }
  } catch (err) {
    logError({
      component: 'actUponRadarFatalError',
      message: 'Unable to check for fatal radar errors',
      error: err,
      stack: err && err.stack,
    });
  }
};
